import {
  CircularProgress,
  Dialog,
  DialogActions,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import SendIcon from "@mui/icons-material/Send";
import { useSelector } from "react-redux";
import { appIcons } from "../../assets";
import DialogHeader from "./DialogHeader";
import InputField from "../InputField";
import { getAllChatsByUserId } from "../../services/chatFn";
import moment from "moment";
import { t } from "i18next";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing("16px"),
    display: "flex",
    flexDirection: "column",
    height: "58vh",
    maxHeight: "58vh",
  },
  "& .MuiPaper-root": {
    width: "500px",
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
}));

const ChatBox = styled("div")(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  border: `1px solid ${theme.palette.divider}`,
  // borderRadius: theme.spacing(1),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  height: "350px",
}));

const Message = styled("div")(({ theme, isUser }) => ({
  display: "flex",
  justifyContent: isUser ? "flex-end" : "flex-start",
  marginBottom: theme.spacing(1),
}));

const MessageBubble = styled("div")(({ theme, isUser }) => ({
  maxWidth: "70%",
  padding: theme.spacing("8px 10px"),
  borderRadius: theme.spacing("8px"),
  backgroundColor: isUser
    ? theme.palette.primary.main
    : theme.palette.grey[300],
  color: isUser ? theme.palette.common.white : theme.palette.text.primary,
}));

const ChatDialogWithWorker = forwardRef(({ socket }, ref) => {
  const messageContainerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [error, setError] = useState(false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const userData = useSelector((state) => state.userReducer.user);
  const [dialogData, setDialogData] = useState(null);

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setDialogData(params);
      setOpen(true);
      fetchAllChats(params.conversationId);
    },
    closeDialog() {
      setOpen(false);
    },
  }));

  useEffect(() => {
    if (dialogData) {
      socket.emit("joinConversationRoom", {
        conversationId: dialogData?.conversationId,
      });

      const handleReceiveMessage = (newMessage) => {
        setMessages((prev) => [...prev, newMessage]);
      };

      socket.on("receiveMessage", handleReceiveMessage);

      return () => {
        socket.off("receiveMessage", handleReceiveMessage);
      };
    }
  }, [socket, dialogData]);

  useEffect(() => {
    if (dialogData) {
      socket.emit("resetUnreadCount", {
        conversationId: dialogData?.conversationId,
        userId: userData._id,
        conversationType: "WorkerWithUser",
      });
    }
  }, [socket, dialogData]);

  useEffect(() => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop =
        messageContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchAllChats = async (conversationId) => {
    try {
      setChatLoading(true);
      const response = await getAllChatsByUserId(conversationId);
      if (response.data.status === "success") {
        setMessages(response.data.data);
      } else {
        setError(true);
      }
    } catch (error) {
      setError(true);
    } finally {
      setError(false);
      setChatLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const sendMessage = () => {
    if (error) return;
    if (!message) return;

    socket.emit("sendMessage", {
      conversationId: dialogData?.conversationId,
      senderId: userData?._id,
      receiverId: dialogData?.companyId,
      senderRole: "User",
      receiverRole: "Worker",
      message,
      conversationType: "WorkerWithUser",
    });

    setMessages((prev) => [
      ...prev,
      {
        conversationId: dialogData?.conversationId,
        senderId: userData?._id,
        receiverId: dialogData?.companyId,
        senderRole: "User",
        receiverRole: "Admin",
        message,
      },
    ]);
    setMessage("");
  };

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={open}
    >
      <DialogHeader
        title={t('liveSupport')}
        secondaryHeading={t('supportAvailable')}
        icon={<img src={appIcons.blackHeadphoneIcon} width={30} />}
        onClose={handleClose}
      />
      <div>
        <ChatBox ref={messageContainerRef}>
          {chatLoading ? (
            <Stack
              height={"100%"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <CircularProgress />
            </Stack>
          ) : (
            messages.map((msg, index) => (
              <Message key={index} isUser={msg.senderId === userData?._id}>
                <MessageBubble isUser={msg.senderId === userData?._id}>
                  <Typography variant="body2">{msg.message}</Typography>
                  <Typography
                    fontSize={"0.65rem"}
                    alignSelf={
                      msg.senderId === userData?._id ? "flex-end" : "flex-start"
                    }
                  >
                    {moment(msg.createdAt).format("MM/DD/YY hh:mm a")}
                  </Typography>
                </MessageBubble>
              </Message>
            ))
          )}
        </ChatBox>
      </div>

      <DialogActions>
        {/* <Stack> */}

        <InputField
          type="text"
          placeholder={`${t('typeYourMessage')}...`}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onkeydown={(e) => e.key === "Enter" && sendMessage()}
          InputEndIcon={
            <SendIcon
              sx={{ color: "primary.main", cursor: "pointer" }}
              onClick={sendMessage}
            />
          }
        />
        {/* </Stack> */}
      </DialogActions>
    </BootstrapDialog>
  );
});

export default ChatDialogWithWorker;
